import { useState } from "react";

import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaInfoCircle } from "react-icons/fa";

import { handleColumnToggle } from "@/utils/handleColumnTogle";
import {
	getIndicatorClassification,
	getIndicatorClassificationColorClassName,
	getIndicatorClassificationScores,
	IndicatorEsfEnum,
} from "@/utils/indicatorsEsf";
import { calculatePercentage } from "@/utils/calculatePercentage";
import { ResumeIndicatorC2ByHealthTeamData } from "@/services/esus/indicator/esf/c2/getResumeIndicatorC2ByHealthTeam";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsfC2HealthTeamsClassificationTableColumns = {
	healthTeamName: Column;
	totalCitizens: Column;
	totalCitizensScore: Column;
	totalCitizensWithTwoYearsInPeriod: Column;
	totalCitizensWithTwoYearsInPeriodScore: Column;
	citizensWithTwoYearsInPeriodClassificationPercentage: Column;
	citizensWithTwoYearsInPeriodClasification: Column;
};

type IndicatorEsfC2HealthTeamsTableProps = {
	healthTeamsClassifications: ResumeIndicatorC2ByHealthTeamData[];
	isLoading: boolean;
};

const indicatorClassificationScores = getIndicatorClassificationScores(IndicatorEsfEnum.C2);

export function IndicatorEsfC2HealthTeamsClassificationTable({
	healthTeamsClassifications,
	isLoading,
}: Readonly<IndicatorEsfC2HealthTeamsTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsfC2HealthTeamsClassificationTableColumns>({
		healthTeamName: { title: "Equipe", visible: true },
		totalCitizens: { title: "Crianças com até 2 anos", visible: true },
		totalCitizensScore: { title: "Pontuação", visible: true },
		totalCitizensWithTwoYearsInPeriod: { title: "Crianças com 2 anos completos", visible: true },
		totalCitizensWithTwoYearsInPeriodScore: { title: "Pontuação", visible: true },
		citizensWithTwoYearsInPeriodClassificationPercentage: { title: "% Classificação", visible: true },
		citizensWithTwoYearsInPeriodClasification: { title: "Classificação", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th
						className={`text-start px-2 ${!columns.healthTeamName.visible && "d-none"}`}
					>
						{columns.healthTeamName.title}
					</th>

					<th
						className={`px-2 ${
							!columns.totalCitizens.visible && "d-none"
						}`}
					>
						{columns.totalCitizens.title}
					</th>
					<th className={`px-2 ${!columns.totalCitizensScore.visible && "d-none"}`}>
						{columns.totalCitizensScore.title}
					</th>
					<th
						className={`px-2 ${
							!columns.totalCitizensWithTwoYearsInPeriod.visible && "d-none"
						}`}
					>
						{columns.totalCitizensWithTwoYearsInPeriod.title}
					</th>
					<th className={`px-2 ${!columns.totalCitizensWithTwoYearsInPeriodScore.visible && "d-none"}`}>
						{columns.totalCitizensWithTwoYearsInPeriodScore.title}
					</th>
					<th className={`px-2 ${!columns.citizensWithTwoYearsInPeriodClassificationPercentage.visible && "d-none"}`}>
						{columns.citizensWithTwoYearsInPeriodClassificationPercentage.title}
					</th>
					<th className={`px-2 ${!columns.citizensWithTwoYearsInPeriodClasification.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.citizensWithTwoYearsInPeriodClasification.title}{" "}
							<CardOverlayTrigger
								header={"Classificação"}
								icon={<FaInfoCircle className="text-muted" />}
							>
								<div className="text-start">
									<div className="text-primary">{`Ótimo: >= ${indicatorClassificationScores.awesome}% e <= ${indicatorClassificationScores.limit}%`}</div>
									<br />
									<div className="text-success">{`Bom: >= ${indicatorClassificationScores.good}% e < ${indicatorClassificationScores.awesome}%`}</div>
									<br />
									<div className="text-warning">
										{`Suficiente: >= ${indicatorClassificationScores.enough}% e < ${indicatorClassificationScores.good}%`}
									</div>
									<br />
									<div className="text-danger">{`Regular: < ${indicatorClassificationScores.enough}%`}</div>
									<br />
								</div>
							</CardOverlayTrigger>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsfC2HealthTeamsClassificationTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsfC2HealthTeamsClassificationTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsfC2HealthTeamsClassificationTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && healthTeamsClassifications.length > 0
					? healthTeamsClassifications.map((healthTeamClassification, index) => (
							<tr
								className="text-center"
								key={`health-team-classification-table-row-${index + 1}`}
							>
								<td
									className={`text-start px-2 ${
										!columns.healthTeamName.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{healthTeamClassification.healthTeam?.name ?? ""}</div>
										<div className="text-muted">
											{healthTeamClassification.healthTeam?.ine ?? ""}
										</div>
									</div>
								</td>

								<td
									className={`px-2 ${
										!columns.totalCitizens.visible &&
										"d-none"
									}`}
								>
									{healthTeamClassification.totalCitizens ??
										0}
								</td>
								<td className={`px-2 ${!columns.totalCitizensScore.visible && "d-none"}`}>
									{healthTeamClassification.totalCitizensScore ??
										0}
								</td>
								<td
									className={`px-2 ${
										!columns.totalCitizensWithTwoYearsInPeriod.visible &&
										"d-none"
									}`}
								>
									{healthTeamClassification.totalCitizensWithTwoYearsInPeriod ??
										0}
								</td>
								<td className={`px-2 ${!columns.totalCitizensWithTwoYearsInPeriodScore.visible && "d-none"}`}>
									{healthTeamClassification.totalCitizensWithTwoYearsInPeriodScore ??
										0}
								</td>
								<td
									className={`px-2 ${
										!columns.citizensWithTwoYearsInPeriodClassificationPercentage.visible && "d-none"
									}`}
								>
									{`${calculatePercentage(
										healthTeamClassification.totalCitizensWithTwoYearsInPeriodScore,
										healthTeamClassification.maxCitizensWithTwoYearsInPeriodScore
									)}%`}
								</td>
								<td
									className={`text-nowrap px-2 ${
										!columns.citizensWithTwoYearsInPeriodClasification.visible && "d-none"
									}`}
								>
									<Badge
										bg={getIndicatorClassificationColorClassName(
											IndicatorEsfEnum.C2,
											calculatePercentage(
												healthTeamClassification.totalCitizensWithTwoYearsInPeriodScore,
												healthTeamClassification.maxCitizensWithTwoYearsInPeriodScore
											)
										)}
									>
										{getIndicatorClassification(
											IndicatorEsfEnum.C2,
											calculatePercentage(
												healthTeamClassification.totalCitizensWithTwoYearsInPeriodScore,
												healthTeamClassification.maxCitizensWithTwoYearsInPeriodScore
											)
										)}
									</Badge>
								</td>
								<td></td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
